/**
 * M-21 (Daniel 2026-08-04): per-device push notification card.
 *
 * Extracted from Team.tsx the same day it was born: the /team page
 * redirects non-manager roles away, and the people who most need push
 * on their phones are the agents. This shared card renders in two
 * places with two temperaments:
 *
 *   - Team page ("My profile"): always visible, full settings card.
 *   - Home page (compact mode): visible ONLY while there is something
 *     for the user to do (enable, install, or unblock). Once push is on,
 *     or the browser cannot do push at all, it disappears rather than
 *     squatting on the dashboard forever.
 */
import { useEffect, useState } from 'react';
import { disablePush, enablePush, getPushState, type PushStatus } from '../lib/push';
import { Button, FormError } from './FormPrimitives';

export default function PushNotificationsCard({ compact = false }: { compact?: boolean }) {
  // REPO RULE: every hook above any conditional return (React error #310).
  const [status, setStatus] = useState<PushStatus | 'loading'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getPushState().then((s) => {
      if (alive) setStatus(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Compact mode is a prompt, not a settings panel: nothing actionable
  // means nothing rendered. This return sits BELOW every hook on purpose.
  if (compact && (status === 'loading' || status === 'on' || status === 'unsupported')) {
    return null;
  }

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (status === 'on') {
        await disablePush();
      } else {
        // enablePush must run inside this click handler; the permission
        // prompt is gesture-gated on iOS and Chrome both.
        await enablePush();
      }
      setStatus(await getPushState());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setStatus(await getPushState());
    } finally {
      setBusy(false);
    }
  };

  const statusLabel: Record<PushStatus | 'loading', string> = {
    loading: 'Checking this device…',
    unsupported: 'Not supported in this browser',
    'ios-needs-install': 'Needs the Home Screen app',
    denied: 'Blocked in browser settings',
    off: 'Off on this device',
    on: 'On for this device',
  };

  const canToggle = status === 'on' || status === 'off';
  const showIosGuidance = status === 'ios-needs-install';

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: compact ? '14px 18px' : '20px 24px',
        marginBottom: compact ? 16 : 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-heading)', margin: 0 }}>
          Push notifications
        </h2>
        <span
          style={{
            fontSize: 12,
            color: status === 'on' ? 'var(--success)' : 'var(--text-muted)',
            fontWeight: 500,
          }}
        >
          {statusLabel[status]}
        </span>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Get a notification on this device for new leads, missed calls, and client replies.
      </p>

      <FormError message={error} />

      {showIosGuidance && (
        <p
          style={{
            fontSize: 13,
            color: 'var(--text-body)',
            background: 'var(--bg-page)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md, 8px)',
            padding: '10px 12px',
            margin: '0 0 12px',
          }}
        >
          On iPhone, open TR in Safari, tap Share, then Add to Home Screen. Open TR from the
          new icon, come back here, and tap Enable.
        </p>
      )}

      {status === 'denied' && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          Notifications were blocked for this site. Re-allow them in your browser or iOS
          notification settings, then reload this page.
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={toggle} disabled={!canToggle || busy}>
          {busy ? 'Working…' : status === 'on' ? 'Disable on this device' : 'Enable notifications'}
        </Button>
      </div>
    </section>
  );
}
